import { z } from "zod";
import type { CrmContact, CrmRecordType } from "@/lib/api/crm/masters";
import type { CrmRules } from "@/lib/api/crm/rules";
import type { AvailableOnboardingInstanceInfo } from "@/lib/api/customer";
import { formatPhoneForDisplay } from "@/lib/validation/phoneNumber";
import {
  recordTypeFields,
  type AddressFormRow,
  type ContactFormRow,
  type OnboardingSelection,
  type RecordFormTypeFields,
  type RecordFormValues,
} from "./recordFormSchema";
import { emptyAddressRow } from "./recordFormValues";
import { catalogTagId } from "./tagOptions";

// Each key is read on its own: one malformed value doesn't drop the rest.
const optionalText = z.string().nullish().catch(undefined);
const optionalTextList = z.array(z.string()).nullish().catch(undefined);

/** The "New Client" onboarding trigger_meta_data keys the form fills from (the same ones ManageCustomers used). */
const OnboardingMetaDataSchema = z.object({
  customer_name: optionalText,
  customer_address: optionalText,
  customer_address_line_one: optionalText,
  customer_address_line_two: optionalText,
  customer_city: optionalText,
  customer_state: optionalText,
  customer_zipcode: optionalText,
  customer_country: optionalText,
  customer_county: optionalText,
  trader_id: optionalText,
  email: optionalText,
  phone_number: optionalText,
  notes: optionalText,
  partner_names: optionalTextList,
  shirt_sizes: optionalTextList,
  number_of_acres: z.union([z.string(), z.number()]).nullish().catch(undefined),
  bol_sequence: optionalText,
}).catch({});

type OnboardingMetaData = z.infer<typeof OnboardingMetaDataSchema>;

/** The instance's customer_name, trimmed; null when it has none. */
export function onboardingCustomerName(instance: AvailableOnboardingInstanceInfo): string | null {
  const name = OnboardingMetaDataSchema.parse(instance.trigger_meta_data).customer_name;
  return name && name.trim() !== "" ? name.trim() : null;
}

/** The form values an onboarding instance sets; everything else is left as typed. */
export interface OnboardingPrefill {
  onboarding: OnboardingSelection;
  /** null leaves the name as is. */
  long_name: string | null;
  fields: Partial<RecordFormTypeFields>;
  contacts: ContactFormRow[];
  addresses: AddressFormRow[];
}

const present = (value: string | null | undefined): value is string => !!value && value.trim() !== "";

/** number_of_acres as whole acres (original_acres is an integer); null when it isn't one. */
function wholeAcres(value: string | number | null | undefined): number | null {
  const text = typeof value === "number" ? String(value) : value?.trim() ?? "";
  return /^\d+$/.test(text) ? Number(text) : null;
}

/** The mailing address: the one-line customer_address, else the split address fields. null when there is none. */
function mailingAddress(metaData: OnboardingMetaData, mailingTagId: string): AddressFormRow | null {
  const lines = present(metaData.customer_address)
    ? { address_line_one: metaData.customer_address }
    : {
      address_line_one: metaData.customer_address_line_one ?? "",
      address_line_two: metaData.customer_address_line_two ?? "",
      city: metaData.customer_city ?? "",
      state: metaData.customer_state ?? "",
      zipcode: metaData.customer_zipcode ?? "",
      country: metaData.customer_country ?? "",
      county: metaData.customer_county ?? "",
    };
  if (!Object.values(lines).some(present)) return null;
  return { ...emptyAddressRow(), ...lines, tag_ids: [mailingTagId] };
}

/**
 * What picking an onboarding instance fills in: the name, the notes and trader (when the type has them),
 * a Primary email and phone, and a Mailing address. Each replaces the form's current Primary contact of
 * that kind or Mailing address, so picking another instance doesn't add a second one.
 */
export function onboardingPrefill(
  rules: CrmRules,
  recordType: CrmRecordType,
  values: RecordFormValues,
  instance: AvailableOnboardingInstanceInfo,
): OnboardingPrefill {
  const metaData = OnboardingMetaDataSchema.parse(instance.trigger_meta_data);
  const typeFields = recordTypeFields(rules, recordType);

  const fields = { ...values.fields };
  if (present(metaData.notes) && typeFields.includes("notes")) fields.notes = metaData.notes;
  if (present(metaData.trader_id) && typeFields.includes("trader_id")) fields.trader_id = metaData.trader_id;

  const primaryTagId = catalogTagId(rules, "contact", "primary");
  const primaryContacts: ContactFormRow[] = primaryTagId
    ? [
      ...(present(metaData.email)
        ? [{ id: null, kind: "email" as const, value: metaData.email.trim(), label: "", tag_ids: [primaryTagId] }]
        : []),
      ...(present(metaData.phone_number)
        ? [{ id: null, kind: "phone" as const, value: formatPhoneForDisplay(metaData.phone_number.trim()), label: "", tag_ids: [primaryTagId] }]
        : []),
    ]
    : [];
  const replacedKinds = new Set<CrmContact["kind"]>(primaryContacts.map((contact) => contact.kind));
  const contacts = [
    ...values.contacts.filter((contact) => !(replacedKinds.has(contact.kind) && primaryTagId && contact.tag_ids.includes(primaryTagId))),
    ...primaryContacts,
  ];

  const mailingTagId = catalogTagId(rules, "address", "mailing");
  const address = mailingTagId ? mailingAddress(metaData, mailingTagId) : null;
  const addresses = address
    ? [...values.addresses.filter((row) => !(mailingTagId && row.tag_ids.includes(mailingTagId))), address]
    : values.addresses;

  return {
    onboarding: {
      instance_id: instance.id,
      partner_names: metaData.partner_names ?? [],
      shirt_sizes: metaData.shirt_sizes ?? [],
      original_acres: wholeAcres(metaData.number_of_acres),
      bol_sequence: present(metaData.bol_sequence) ? metaData.bol_sequence : null,
    },
    long_name: onboardingCustomerName(instance),
    fields,
    contacts,
    addresses,
  };
}
